import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  documentSignatureService,
  SignatureRuleCode,
  SignatureTypeCode,
  type CreateSignatureRequestPayload,
  type SignaturePlacementInputDto,
  type SignerInputDto,
} from '../../../services/documentSignatureService';
import { PdfSignaturePlacementEditor } from '../../Signature/PdfSignaturePlacementEditor';
import { SignatureExpiresAtField } from '../../Signature/SignatureExpiresAtField';
import { useToast } from '../../UI/ToastProvider';
import { parseSignatureExpiresAtInput } from '../../../utils/signatureExpiresAtUtils';

type SignerRow = SignerInputDto & { key: string; placement?: SignaturePlacementInputDto | null };

function newKey(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function emptySigner(): SignerRow {
  return {
    key: newKey(),
    name: '',
    email: '',
    signatureType: SignatureTypeCode.Electronic,
    isRequired: true,
  };
}

export interface StandaloneSignatureRequestModalProps {
  open: boolean;
  documentId: string;
  documentVersionId?: string | null;
  nodeType?: string;
  /** Exibe campo de prazo quando a assinatura não é coberta por etapa Signature do workflow. */
  showExpiresAtField?: boolean;
  onClose: () => void;
  onCreated: (signatureRequestId?: string) => void | Promise<void>;
}

export const StandaloneSignatureRequestModal: React.FC<StandaloneSignatureRequestModalProps> = ({
  open,
  documentId,
  documentVersionId,
  nodeType,
  showExpiresAtField = false,
  onClose,
  onCreated,
}) => {
  const { addToast } = useToast();
  const [signers, setSigners] = useState<SignerRow[]>([emptySigner()]);
  const [signatureRule, setSignatureRule] = useState<number>(SignatureRuleCode.All);
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<'signers' | 'placement'>('signers');
  const [activePlacementKey, setActivePlacementKey] = useState<string | null>(null);
  const isPdfDocument = nodeType === 'pdf';
  const canPositionSignatures = isPdfDocument;

  useEffect(() => {
    if (!open)
    {
      return;
    }
    setSigners([emptySigner()]);
    setSignatureRule(SignatureRuleCode.All);
    setExpiresAt('');
    setStep('signers');
    setActivePlacementKey(null);
  }, [open, documentId]);

  const allCertificateSigners =
    signers.filter((s) => s.name.trim() && s.email.trim()).length > 0 &&
    signers
      .filter((s) => s.name.trim() && s.email.trim())
      .every((s) => s.signatureType === SignatureTypeCode.Certificate);

  const addRow = () => {
    setSigners((prev) => [...prev, emptySigner()]);
  };

  const removeRow = (key: string) => {
    setSigners((prev) => (prev.length <= 1 ? prev : prev.filter((s) => s.key !== key)));
  };

  const updateRow = (key: string, patch: Partial<SignerRow>) => {
    setSigners((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  };

  const handleSubmit = async () => {
    const valid = signers.filter((s) => s.name.trim() && s.email.trim());
    if (valid.length === 0)
    {
      addToast('Informe ao menos um signatário com nome e e-mail.', 'error');
      return;
    }

    const payloadSigners: SignerInputDto[] = valid.map((s) => ({
      name: s.name.trim(),
      email: s.email.trim(),
      signatureType: s.signatureType,
      isRequired: s.isRequired !== false,
      placement: s.placement ?? undefined,
    }));

    const minSignatures =
      signatureRule === SignatureRuleCode.Any
        ? Math.min(Math.max(1, valid.filter((s) => s.isRequired !== false).length), valid.length)
        : valid.filter((s) => s.isRequired !== false).length || valid.length;

    let expiresAtIso: string | null = null;
    if (showExpiresAtField && expiresAt.trim())
    {
      const parsedExpiresAt = parseSignatureExpiresAtInput(expiresAt);
      if (!parsedExpiresAt.ok)
      {
        addToast(parsedExpiresAt.message, 'error');
        return;
      }

      expiresAtIso = parsedExpiresAt.iso;
    }

    const body: CreateSignatureRequestPayload = {
      documentId,
      documentVersionId: documentVersionId ?? null,
      signatureRule,
      minSignatures,
      expiresAt: expiresAtIso,
      signers: payloadSigners,
    };

    setSubmitting(true);
    try {
      const response = await documentSignatureService.createSignatureRequest(body);
      addToast('Solicitação de assinatura criada. Use "Enviar para assinatura" para notificar os signatários.', 'success');
      await onCreated(response.id);
      onClose();
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Não foi possível criar a solicitação.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !submitting && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full rounded-lg bg-white shadow-xl ${canPositionSignatures && step === 'placement' ? 'max-w-4xl' : 'max-w-2xl'}`}>
                <div className="border-b px-6 py-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Solicitar assinatura
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-500">
                    Documento publicado: configure os signatários. Depois envie os convites com o botão &quot;Enviar para assinatura&quot;.
                  </p>
                </div>

                <div className="max-h-[60vh] space-y-4 overflow-y-auto px-6 py-4">
                  {allCertificateSigners && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      <strong>Assinatura eletrônica qualificada.</strong> Todos os signatários usarão certificado digital (ICP-Brasil).
                    </div>
                  )}

                  {canPositionSignatures && step === 'placement' && (
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">Signatário para posicionar</label>
                      <select
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        value={activePlacementKey ?? ''}
                        onChange={(e) => setActivePlacementKey(e.target.value || null)}
                      >
                        <option value="">Selecione…</option>
                        {signers
                          .filter((s) => s.name.trim() && s.email.trim())
                          .map((s) => (
                            <option key={s.key} value={s.key}>
                              {s.name}
                            </option>
                          ))}
                      </select>
                      {activePlacementKey && isPdfDocument && (
                        <PdfSignaturePlacementEditor
                          documentId={documentId}
                          signerName={signers.find((s) => s.key === activePlacementKey)?.name ?? ''}
                          placement={signers.find((s) => s.key === activePlacementKey)?.placement}
                          onPlacementChange={(placement) => {
                            updateRow(activePlacementKey, { placement });
                          }}
                        />
                      )}
                    </div>
                  )}

                  {step === 'signers' && (
                  <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Regra de assinatura</label>
                    <select
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      value={signatureRule}
                      onChange={(e) => setSignatureRule(Number(e.target.value))}
                      disabled={submitting}
                    >
                      <option value={SignatureRuleCode.All}>Todos devem assinar</option>
                      <option value={SignatureRuleCode.Any}>Mínimo de assinaturas (qualquer)</option>
                    </select>
                  </div>

                  {showExpiresAtField && (
                    <SignatureExpiresAtField
                      id="signature-expires-at"
                      value={expiresAt}
                      onChange={setExpiresAt}
                      disabled={submitting}
                    />
                  )}

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Signatários</span>
                      <button
                        type="button"
                        onClick={addRow}
                        disabled={submitting}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                      >
                        + Adicionar
                      </button>
                    </div>

                    {signers.map((s) => (
                      <div key={s.key} className="rounded-md border border-gray-200 p-3 space-y-2">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <div>
                            <label className="text-xs text-gray-600">Nome</label>
                            <input
                              className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              value={s.name}
                              onChange={(e) => updateRow(s.key, { name: e.target.value })}
                              disabled={submitting}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">E-mail</label>
                            <input
                              type="email"
                              className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              value={s.email}
                              onChange={(e) => updateRow(s.key, { email: e.target.value })}
                              disabled={submitting}
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="text-xs text-gray-600">Tipo</label>
                            <select
                              className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              value={s.signatureType}
                              onChange={(e) => updateRow(s.key, { signatureType: Number(e.target.value) })}
                              disabled={submitting}
                            >
                              <option value={SignatureTypeCode.Electronic}>Eletrônica</option>
                              <option value={SignatureTypeCode.Handwritten}>Manuscrita</option>
                              <option value={SignatureTypeCode.Certificate}>Certificado</option>
                            </select>
                          </div>
                        </div>
                        {signers.length > 1 && (
                          <div className="text-right">
                            <button
                              type="button"
                              className="text-xs text-red-600 hover:underline"
                              onClick={() => removeRow(s.key)}
                              disabled={submitting}
                            >
                              Remover
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  </>
                  )}
                </div>

                <div className="flex justify-end gap-2 border-t px-6 py-4">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  {canPositionSignatures && step === 'signers' && (
                    <button
                      type="button"
                      onClick={() => {
                        const valid = signers.filter((s) => s.name.trim() && s.email.trim());
                        if (valid.length === 0)
                        {
                          addToast('Informe ao menos um signatário.', 'error');
                          return;
                        }
                        setActivePlacementKey(valid[0]?.key ?? null);
                        setStep('placement');
                      }}
                      disabled={submitting}
                      className="rounded-md border border-indigo-300 px-4 py-2 text-sm font-medium text-indigo-800 hover:bg-indigo-50 disabled:opacity-50"
                    >
                      Posicionar assinaturas
                    </button>
                  )}
                  {canPositionSignatures && step === 'placement' && (
                    <button
                      type="button"
                      onClick={() => setStep('signers')}
                      disabled={submitting}
                      className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Voltar
                    </button>
                  )}
                  {(!canPositionSignatures || step === 'placement') && (
                    <button
                      type="button"
                      onClick={() => void handleSubmit()}
                      disabled={submitting}
                      className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {submitting ? 'Salvando...' : 'Criar solicitação'}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
